import { makeAutoObservable } from "mobx";
import { signInFunnel, type SignInSource } from "@/core/signInFunnel";
import { isPromptQuiet, readPromptDismissedAt, writePromptDismissedAt } from "@/core/promptQuiet";
import type { SignInIntent } from "@/core/signInIntent";

const STORAGE_KEY = "hp_sign_in";

/**
 * Sign-in is switched on for everyone by `VITE_SIGN_IN=true` (set in the CI workflow).
 * Without it, `?signin=1` turns it on in
 * this browser (remembered), and `?signin=0` turns it off again, so it can be tested in production.
 */
export const readSignInEnabled = (): boolean => {
    if (import.meta.env.VITE_SIGN_IN === "true") return true;
    if (typeof window === "undefined") return false;

    try {
        const requested = new URLSearchParams(window.location.search).get("signin");
        if (requested === "1") localStorage.setItem(STORAGE_KEY, "1");
        if (requested === "0") localStorage.removeItem(STORAGE_KEY);
        return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
        // Storage can be blocked; sign-in then stays hidden
        return false;
    }
};

export class AppStore {
    showSignIn = false;
    signInEnabled = readSignInEnabled();
    /** When a sign-in prompt was last closed; one quiet time for all of them (see `promptQuiet`) */
    signInPromptDismissedAt = readPromptDismissedAt();
    /** The home banner is showing: it is the one prompt on the page, so the player line stays away */
    homeBannerVisible = false;

    constructor() {
        // The pending intent holds functions to call later: it stays as it is, only its message is watched
        makeAutoObservable<AppStore, "pendingIntent">(this, { pendingIntent: false });
    }

    /** Closed a sign-in prompt recently: none of them is shown, and nothing is suggested after a save */
    get signInPromptDismissed(): boolean {
        return isPromptQuiet(this.signInPromptDismissedAt);
    }

    /** What the dialog says about what the listener was trying, if anything */
    signInMessage: string | null = null;
    /** What to do once they are in */
    private pendingIntent: SignInIntent | null = null;
    /** One suggestion to sign in after a save per visit is enough */
    private signInNudged = false;

    openSignIn(source: SignInSource, intent?: SignInIntent) {
        this.showSignIn = true;
        this.pendingIntent = intent ?? null;
        this.signInMessage = intent?.message ?? null;
        signInFunnel.opened(source);
    }

    /** The pending intent, handed over once */
    takeSignInIntent(): SignInIntent | null {
        const intent = this.pendingIntent;
        this.pendingIntent = null;
        return intent;
    }

    /**
     * True when it is fine to suggest signing in after a save: sign-in is open, the listener has not
     * said no to the home prompt, and it has not been suggested during this visit.
     */
    takeSignInNudge(): boolean {
        if (!this.signInEnabled || this.signInPromptDismissed || this.signInNudged) return false;
        this.signInNudged = true;
        return true;
    }

    /** Closing the dialog after signing in is not abandoning it: `signedIn` has already ended the visit */
    closeSignIn() {
        this.showSignIn = false;
        signInFunnel.closed();
    }

    dismissSignInPrompt() {
        this.signInPromptDismissedAt = Date.now();
        writePromptDismissedAt(this.signInPromptDismissedAt);
    }

    setHomeBannerVisible(visible: boolean) {
        this.homeBannerVisible = visible;
    }
}
